import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { redApi, operatoriAvvisoApi, whatsappApi } from '../../services/api'

const XLSX_MIMETYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

interface OperatoreAvviso { id: string; nome: string; email?: string | null; telefono_whatsapp?: string | null; attivo: boolean }

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

interface SheetGroup { label: string; sheets: { key: string; label: string }[] }

const SHEET_GROUPS: SheetGroup[] = [
  { label: 'Grafici', sheets: [
    { key: 'grafici_portfolio', label: 'Grafici - Portfolio' },
    { key: 'grafici_economia', label: 'Grafici - Economia' },
    { key: 'grafici_operativo', label: 'Grafici - Operativo' },
  ] },
  { label: 'Anagrafiche & dati immobile', sheets: [
    { key: 'immobili', label: 'Anagrafiche Immobili' },
    { key: 'dati_catastali', label: 'Dati Catastali' },
  ] },
  { label: 'Economico', sheets: [
    { key: 'pagamenti', label: 'Pagamenti' },
    { key: 'sal_list', label: 'SAL' },
    { key: 'finanziamenti', label: 'Finanziamenti' },
    { key: 'computo_soa', label: 'Computo SOA' },
  ] },
  { label: 'Documentale & pratiche', sheets: [
    { key: 'documenti', label: 'Documenti' },
    { key: 'scadenze', label: 'Scadenze' },
    { key: 'agenda_note', label: 'Agenda Note' },
    { key: 'appalti', label: 'Appalti' },
    { key: 'pratiche_edilizie', label: 'Pratiche Edilizie' },
    { key: 'pratiche_usr', label: 'Pratiche USR' },
    { key: 'rendicontazioni', label: 'Rendicontazioni' },
  ] },
  { label: 'Altro', sheets: [
    { key: 'foto_cantiere', label: 'Foto Cantiere' },
    { key: 'valutazioni', label: 'Valutazioni' },
    { key: 'badge_accessi', label: 'Badge Accessi' },
    { key: 'firme_digitali', label: 'Firma Digitale' },
    { key: 'opportunita', label: 'Opportunità' },
    { key: 'professionisti', label: 'Professionisti' },
    { key: 'professionisti_immobili', label: 'Professionisti-Immobili' },
    { key: 'gruppi_immobiliari', label: 'Gruppi Immobiliari' },
    { key: 'condomini_quote', label: 'Quote Condominio' },
    { key: 'imeco_checklist', label: 'Checklist IMECO' },
    { key: 'alert_log', label: 'Alert Log' },
    { key: 'operatori_avviso', label: 'Operatori Avviso' },
    { key: 'tipologie_documento_custom', label: 'Tipologie Documento' },
  ] },
]

const SHEET_KEYS_DEFAULT = ['grafici_portfolio', 'grafici_economia', 'grafici_operativo', 'immobili', 'dati_catastali']

const ExportXlsxModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(SHEET_KEYS_DEFAULT))
  const [exporting, setExporting] = useState(false)
  const [step, setStep] = useState<'fogli' | 'destinatari'>('fogli')
  const [canale, setCanale] = useState<'whatsapp' | 'email'>('whatsapp')
  const [operatori, setOperatori] = useState<OperatoreAvviso[]>([])
  const [destinatariSelezionati, setDestinatariSelezionati] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState(false)

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  const nomeFile = `red_export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`

  const generaXlsx = () => redApi.export.xlsx({ sheets: Array.from(selected) })

  const handleScarica = async () => {
    if (selected.size === 0) { alert('Seleziona almeno un foglio'); return }
    setExporting(true)
    try {
      const res = await generaXlsx()
      const blob = new Blob([res.data], { type: XLSX_MIMETYPE })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomeFile
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      onClose()
    } catch {
      alert('Errore durante l\'export')
    } finally {
      setExporting(false)
    }
  }

  const apriStepDestinatari = async (canaleScelto: 'whatsapp' | 'email') => {
    if (selected.size === 0) { alert('Seleziona almeno un foglio'); return }
    setCanale(canaleScelto)
    setDestinatariSelezionati(new Set())
    setStep('destinatari')
    try {
      const res = await operatoriAvvisoApi.list()
      const campo = canaleScelto === 'whatsapp' ? 'telefono_whatsapp' : 'email'
      setOperatori(res.data.filter((o: OperatoreAvviso) => o[campo]))
    } catch {
      toast.error('Errore caricamento operatori avviso')
    }
  }

  const toggleDestinatario = (id: string) => setDestinatariSelezionati(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleInvia = async () => {
    if (destinatariSelezionati.size === 0) return
    setSending(true)
    try {
      const destinatari = operatori.filter(o => destinatariSelezionati.has(o.id))
      if (canale === 'whatsapp') {
        const res = await generaXlsx()
        const blob = new Blob([res.data], { type: XLSX_MIMETYPE })
        const base64 = await blobToBase64(blob)
        let ok = 0
        for (const d of destinatari) {
          try {
            await whatsappApi.sendDocument(d.telefono_whatsapp as string, base64, nomeFile, 'Export RED — Ricostruzione Marche', XLSX_MIMETYPE)
            ok++
          } catch {
            toast.error(`Invio fallito a ${d.nome}`)
          }
        }
        if (ok > 0) toast.success(`XLSX inviato a ${ok} destinatari`)
      } else {
        const res = await redApi.export.inviaEmail({ sheets: Array.from(selected), destinatari: destinatari.map(d => d.email as string) })
        if (res.data.inviati > 0) toast.success(`XLSX inviato via email a ${res.data.inviati} destinatari`)
        if (res.data.inviati < res.data.totale) toast.error(`${res.data.totale - res.data.inviati} invii falliti`)
      }
      onClose()
    } catch {
      toast.error('Errore durante l\'invio')
    } finally {
      setSending(false)
    }
  }

  if (step === 'destinatari') {
    const campoContatto = (o: OperatoreAvviso) => canale === 'whatsapp' ? o.telefono_whatsapp : o.email
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="card w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-lg font-bold text-gray-900">{canale === 'whatsapp' ? '💬 Invia XLSX via WhatsApp' : '✉️ Invia XLSX via Email'}</h3>
            <p className="text-sm text-gray-500 mt-0.5">Seleziona i destinatari (lista Operatori Avviso — Impostazioni RED)</p>
          </div>
          <div className="p-5 space-y-1.5 max-h-72 overflow-auto">
            {operatori.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                {canale === 'whatsapp' ? 'Nessun operatore con numero WhatsApp configurato' : 'Nessun operatore con email configurata'}
              </p>
            ) : (
              operatori.map(o => (
                <label key={o.id} className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm ${!o.attivo ? 'opacity-50' : ''}`}>
                  <input
                    type="checkbox"
                    checked={destinatariSelezionati.has(o.id)}
                    onChange={() => toggleDestinatario(o.id)}
                  />
                  <span>{o.nome}</span>
                  {!o.attivo && <span className="text-xs text-gray-400">(disattivato)</span>}
                  <span className="text-xs text-gray-400 ml-auto">{campoContatto(o)}</span>
                </label>
              ))
            )}
          </div>
          <div className="p-5 border-t border-gray-100 flex gap-3">
            <button onClick={() => setStep('fogli')} className="btn-secondary flex-1">Indietro</button>
            <button
              onClick={handleInvia}
              disabled={destinatariSelezionati.size === 0 || sending}
              className="btn-primary flex-1"
            >
              {sending ? 'Invio...' : `Invia (${destinatariSelezionati.size})`}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Esporta XLSX — scegli i fogli</h3>
          <p className="text-sm text-gray-500 mt-0.5">Seleziona i fogli da includere nel file esportato</p>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {SHEET_GROUPS.map(group => (
            <div key={group.label}>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{group.label}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {group.sheets.map(s => (
                  <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(s.key)}
                      onChange={() => toggle(s.key)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-gray-100 flex flex-wrap gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button onClick={() => apriStepDestinatari('email')} disabled={exporting} className="btn-secondary flex-1">
            ✉️ Email
          </button>
          <button onClick={() => apriStepDestinatari('whatsapp')} disabled={exporting} className="btn-secondary flex-1">
            💬 WhatsApp
          </button>
          <button onClick={handleScarica} disabled={exporting} className="btn-primary flex-1">
            {exporting ? 'Export...' : `⬇️ Scarica (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  )
}

const ExportXlsxButton: React.FC<{ large?: boolean }> = ({ large }) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          large
            ? 'px-6 py-3 text-base bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-sm'
            : 'px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5'
        }
      >
        📊 Esporta
      </button>
      {open && <ExportXlsxModal onClose={() => setOpen(false)} />}
    </>
  )
}

export default ExportXlsxButton
